import React from "react";

import { Box, Typography } from "@mui/material";

import Waveform from "../Waveform";
import BackgroundLayer from "./BackgroundLayer";
import { AudiogramEditorState } from "../../types";

interface LightEditorialProps {
  state: AudiogramEditorState;
  scale?: number;
  audioLevels?: number[];
  isPlaying?: boolean;
}

export default function LightEditorial({
  state,
  scale = 1,
  audioLevels,
  isPlaying,
}: LightEditorialProps) {
  const {
    quote,
    fontFamily,
    fontSize,
    showName,
    guestName,
    accentColor,
    waveformStyle,
    waveformColor,
    waveformHeight,
    animated,
    tags,
    textColor,
    showDate,
    date,
    backgroundImage,
    backgroundOverlay,
    backgroundFit,
  } = state;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#FAFAF8",
        border: "0.5px solid #E8E6E0",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 3 * scale,
        boxSizing: "border-box",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {backgroundImage && (
        <BackgroundLayer
          image={backgroundImage}
          overlay={backgroundOverlay}
          overlayColor="#FAFAF8"
          fit={backgroundFit}
        />
      )}

      {/* Content layer — above background */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        {/* Show name + accent rule */}
        <Box>
          <Typography
            sx={{
              fontSize: 10 * scale,
              fontWeight: 600,
              color: "rgba(0,0,0,0.4)",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              lineHeight: 1,
              mb: 1 * scale,
            }}
          >
            {showName}
          </Typography>
          <Box
            sx={{
              width: 32 * scale,
              height: 2 * scale,
              backgroundColor: accentColor || "#111",
              borderRadius: "1px",
            }}
          />
        </Box>

        {/* Quote — generous spacing, serif italic */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "flex-start",
            pt: 3 * scale,
            px: 0.5 * scale,
          }}
        >
          <Typography
            sx={{
              color: textColor,
              fontSize: fontSize * scale,
              fontWeight: 400,
              fontFamily:
                fontFamily ||
                "'Georgia', 'Playfair Display', 'Times New Roman', serif",
              fontStyle: "italic",
              lineHeight: 1.7,
              letterSpacing: "-0.005em",
              display: "-webkit-box",
              WebkitLineClamp: 12,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {quote}
          </Typography>
        </Box>

        {/* Waveform — bottom third */}
        <Box sx={{ px: 1 * scale, mb: 2 * scale }}>
          <Waveform
            style={waveformStyle}
            color={waveformColor || accentColor}
            height={waveformHeight}
            animated={animated}
            barCount={56}
            levels={audioLevels}
            playing={isPlaying}
          />
        </Box>

        {/* Thin progress-style line */}
        <Box
          sx={{
            height: "1px",
            backgroundColor: "#E8E6E0",
            mb: 1.5 * scale,
          }}
        />

        {/* Bottom row: guest + tags left, date right */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "#111",
                fontSize: 13 * scale,
                fontWeight: 700,
                lineHeight: 1.3,
              }}
            >
              {guestName}
            </Typography>
            {tags && tags.length > 0 && (
              <Typography
                sx={{
                  color: "rgba(0,0,0,0.4)",
                  fontSize: 10 * scale,
                  fontWeight: 500,
                  mt: 0.3 * scale,
                }}
              >
                {tags.join(" · ")}
              </Typography>
            )}
          </Box>
          {showDate && date && (
            <Typography
              sx={{
                color: "rgba(0,0,0,0.35)",
                fontSize: 10 * scale,
                fontWeight: 500,
                textAlign: "right",
                lineHeight: 1.3,
              }}
            >
              {date}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
